"use client";

import { useQuery } from "@tanstack/react-query";

import { getDefaultAssistantIdentity } from "@/hooks/assistant/assistantApi";

import type { AssistantIdentity } from "@/hooks/assistant/useAssistantIdentity";

/**
 * Cache key for the default assistant. Exported because the admin "Make
 * default" action must invalidate exactly this key — a second copy of the
 * literal is how a stale default survives the change that was meant to move it.
 */
export const DEFAULT_ASSISTANT_IDENTITY_QUERY_KEY = [
  "default-assistant-identity",
] as const;

/**
 * The one definition of the default-assistant query, shared by this hook and
 * useAssistantIdentity's signed-out branch. Its route is public and is fetched
 * with no credentials, so it never waits for Clerk.
 */
export const defaultAssistantIdentityQuery = {
  queryKey: DEFAULT_ASSISTANT_IDENTITY_QUERY_KEY,
  queryFn: () => getDefaultAssistantIdentity(),
};

/**
 * The default assistant, for pages only a visitor ever sees (/landing, which
 * sends a signed-in member on). Asked for at once, with no Clerk involved: a
 * browser that blocks Clerk still gets the header's assistant (TX-5483).
 * Surfaces a member may see use useAssistantIdentity, which waits for Clerk so
 * the default never flashes in before the member's own.
 */
export function useDefaultAssistantIdentity(): AssistantIdentity {
  const query = useQuery(defaultAssistantIdentityQuery);
  return {
    name: query.data?.name ?? null,
    icon: query.data?.icon ?? null,
    isLoading: query.isLoading,
  };
}
